export interface ProductCatalogImportCommand {
  filename: string;
  csv: string;
  idempotencyKey: string;
  previewFingerprint: string;
  expectedConfigurationVersion: number;
  rowCount: number;
}

export type ProductCatalogImportRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface ProductCatalogImportRecovery {
  ownerIdentityKey: string;
  command: ProductCatalogImportCommand;
  state: ProductCatalogImportRecoveryState;
}

export type ProductCatalogImportRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: ProductCatalogImportRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE"; pendingOutcomeMayExist: boolean };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

export const PRODUCT_CATALOG_IMPORT_RECOVERY_STORAGE_KEY =
  "billiard-erp:product-catalog-import-recovery:v1";

const definiteNoEffectCodes = new Set([
  "AUTH_REQUIRED",
  "AUTH_CREDENTIAL_CONFLICT",
  "AUTHORIZATION_DEFAULT_DENY",
  "AUTHORIZATION_FACTS_NOT_TRUSTED",
  "AUTHORITY_EXPIRED",
  "CATALOG_IMPORT_DUPLICATE_SKU",
  "CATALOG_IMPORT_FORMULA_REJECTED",
  "CATALOG_IMPORT_CONFIGURATION_VERSION_CONFLICT",
  "CATALOG_IMPORT_PREVIEW_EXPIRED",
  "CLOUD_AUTHORIZATION_FAILED",
  "CLOUD_OPERATION_UNAVAILABLE",
  "CONDITIONAL_MODULE_DISABLED",
  "CSRF_REJECTED",
  "CSV_VALIDATION_FAILED",
  "DEMO_AUTH_DISABLED",
  "GATEWAY_IDENTITY_MISMATCH",
  "IDEMPOTENCY_COLLISION",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "ONLINE_SERVICE_REQUIRED",
  "ORIGIN_REJECTED",
  "REQUEST_VALIDATION_FAILED",
  "SHIFT_SESSION_NOT_STARTED",
  "VENUE_NOT_INITIALIZED",
  "WORKFORCE_MUTATION_OUTCOME_UNKNOWN",
]);

let pendingRecovery: ProductCatalogImportRecovery | null = null;
let dispatchingRecovery: ProductCatalogImportRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;

export function productCatalogImportIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && [...value].length >= minimum && [...value].length <= maximum;
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function isProductCatalogImportCommand(value: unknown): value is ProductCatalogImportCommand {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "filename",
      "csv",
      "idempotencyKey",
      "previewFingerprint",
      "expectedConfigurationVersion",
      "rowCount",
    ]) &&
    isBoundedString(value.filename, 1, 255) &&
    value.filename === value.filename.trim() &&
    isBoundedString(value.csv, 1, 262_144) &&
    isBoundedString(value.idempotencyKey, 8, 128) &&
    value.idempotencyKey === value.idempotencyKey.trim() &&
    typeof value.previewFingerprint === "string" &&
    /^[a-f0-9]{16}$/u.test(value.previewFingerprint) &&
    Number.isSafeInteger(value.expectedConfigurationVersion) &&
    (value.expectedConfigurationVersion as number) >= 1 &&
    Number.isSafeInteger(value.rowCount) &&
    (value.rowCount as number) >= 1 &&
    (value.rowCount as number) <= 500
  );
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: ProductCatalogImportCommand,
  state: ProductCatalogImportRecoveryState,
): ProductCatalogImportRecovery {
  return Object.freeze({ ownerIdentityKey, command: Object.freeze({ ...command }), state });
}

function storedRecovery(value: unknown): ProductCatalogImportRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 2 ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(value.recovery.ownerIdentityKey) ||
    !isProductCatalogImportCommand(value.recovery.command) ||
    (value.recovery.state !== "CLAIMED" && value.recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(
    value.recovery.ownerIdentityKey,
    value.recovery.command,
    value.recovery.state,
  );
}

function recoveryEquals(
  left: ProductCatalogImportRecovery,
  right: ProductCatalogImportRecovery,
): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(storage: Storage): ProductCatalogImportRecovery | null {
  const encoded = storage.getItem(PRODUCT_CATALOG_IMPORT_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: ProductCatalogImportRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      PRODUCT_CATALOG_IMPORT_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 2, recovery }),
    );
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function verifyStoredRecovery(recovery: ProductCatalogImportRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(PRODUCT_CATALOG_IMPORT_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovered =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovered)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovered;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readProductCatalogImportRecovery(
  identityKey: string,
): ProductCatalogImportRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable || !isIdentityKey(identityKey)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimProductCatalogImportRecovery(
  identityKey: string,
  command: ProductCatalogImportCommand,
): ProductCatalogImportRecoveryView {
  const current = readProductCatalogImportRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !isProductCatalogImportCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery };
}

function verifyRecoveryForDispatch(
  recovery: ProductCatalogImportRecovery,
  identityKey: string,
): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStoredRecovery(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginProductCatalogImportDispatch(
  recovery: ProductCatalogImportRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verifyRecoveryForDispatch(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishProductCatalogImportDispatch(
  recovery: ProductCatalogImportRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markProductCatalogImportOutcomeUnknown(
  recovery: ProductCatalogImportRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

export function clearProductCatalogImportRecovery(
  recovery: ProductCatalogImportRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredRecovery(access.storage);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(PRODUCT_CATALOG_IMPORT_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(PRODUCT_CATALOG_IMPORT_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function productCatalogImportOutcomeIsUnknown(error: unknown): boolean {
  return !(
    isRecord(error) &&
    typeof error.code === "string" &&
    definiteNoEffectCodes.has(error.code)
  );
}

export function resetProductCatalogImportRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(PRODUCT_CATALOG_IMPORT_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetProductCatalogImportRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
